import { makeStyles } from "tss-react/mui";
import { Theme } from "@mui/material";
import { EDeviceType, getThemeBreakpointByDeviceType } from "@utils/getThemeBreakpointByDeviceType";

interface IParams {
    isDesktop?: boolean;
    isTabletOrDesktop?: boolean;
    isMobileOrTablet?: boolean;
    isMobile?: boolean;
    isSmallMobile?: boolean;
    getBackgroundColor?(theme: Theme): string;
    getIconColor?(theme: Theme): string;
}

export const useStyle = makeStyles<IParams>()((theme, props) => ({
    container: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        width: "100%",
        gap: theme.spacing(8),
        margin: "auto",
        alignItems: "center",
        marginTop: props.isMobile ? theme.spacing(5) : 0,
    },
    containerDark: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        width: "100%",
        gap: theme.spacing(8),
        backgroundColor: theme.palette.background.main,
    },
    containerRow: {
        display: "flex",
        flexDirection: props.isMobile ? "column" : "row",
        justifyContent: "center",
        width: "100%",
        maxWidth: 1280,
        gap: theme.spacing(8),
        margin: "auto",
        alignItems: "center",
    },
    prequal: {
        flexDirection: props.isMobile ? "column" : "row",
        alignItems: props.isMobile ? "center" : "flex-start",
        minHeight: props.isMobile ? "100%" : 540,
        margin: props.isMobile ? 0 : "0 auto",
        width: "100%",
        maxWidth: 1360,
    },
    prequalForm: {
        display: "flex",
        flexDirection: "column",
        maxWidth: props.isMobile ? 375 : undefined,
        gap: theme.spacing(3),
        paddingLeft: props.isMobile ? theme.spacing(2) : theme.spacing(5),
        paddingRight: props.isMobile ? theme.spacing(2) : 0,
        paddingTop: props.isTabletOrDesktop ? theme.spacing(5) : 0,
    },
    header: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(2),
        alignItems: props.isMobile ? "center" : "flex-start",
        textAlign: props.isMobile ? "center" : "start",
    },
    offerPrice: {
        width: props.isMobile ? "100%" : "fit-content",
        display: props.isMobile ? "flex" : "block",
        flexDirection: "column",
        alignItems: "center",
    },
    firstUsageDescription: {
        fontWeight: theme.typography.fontWeightLight,
        fontSize: 24,
        [getThemeBreakpointByDeviceType(theme, EDeviceType.MOBILE)]: {
            fontSize: 20,
        },
    },
    broadbandOfferContainer: {
        display: "flex",
        flexDirection: "column",
        alignItems: props.isMobileOrTablet ? "center" : undefined,
        justifyContent: props.isMobileOrTablet ? "center" : "space-between",
        width: "100%",
        gap: theme.spacing(7),
    },
    broadbandOffer: {
        width: "100%",
        alignItems: "center",
        paddingInline: theme.spacing(2),
    },
    usages: {
        display: "flex",
        alignItems: "center",
        justifyContent: props.isMobile ? "center" : "flex-start",
        gap: theme.spacing(2),
    },
    iconPrimary: {
        width: 25,
        height: 35,
    },
    iconWrapper: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: props.getBackgroundColor?.(theme) ?? theme.palette.tertiary.main,
        borderRadius: "50%",
        minWidth: 60,
        minHeight: 60,
    },
    iconSecondary: {
        color: props.getIconColor?.(theme) ?? theme.palette.secondary.main,
        minWidth: 30,
        minHeight: 30,
    },
    image: {
        width: "100%",
        maxWidth: props.isMobile ? "100%" : 479,
        objectFit: "cover",
    },
    imageBroadband: {
        width: "100%",
        maxWidth: props.isMobile ? "100%" : 600,
        objectFit: "cover",
    },
    imageGum: {
        height: props.isMobile ? undefined : 560,
        width: props.isMobile ? "100%" : undefined,
        objectFit: "cover",
    },
    footer: {
        position: "sticky",
        bottom: 0,
        height: 75,
        width: "100%",
        backgroundColor: theme.palette.primary.main,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
    },
    inlineContainer: {
        display: "flex",
        flexDirection: "row",
        gap: theme.spacing(4),
        [theme.breakpoints.down("lg")]: {
            flexDirection: "column",
        },
    },
    marketing: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        margin: "0 auto",
        maxWidth: props.isMobile ? 632 : 1280,
        padding: theme.spacing(7, 2),
        gap: theme.spacing(5),
    },
    whyBlock: {
        display: "flex",
        flexDirection: props.isMobileOrTablet ? "row" : "column",
        justifyContent: props.isMobileOrTablet ? "left" : "flex-start",
        width: props.isMobileOrTablet ? "100%" : "auto",
        alignItems: "center",
        gap: theme.spacing(2),
        maxWidth: props.isMobileOrTablet ? "100%" : 280,
    },
    marketingTitle: {
        textAlign: props.isMobileOrTablet ? "left" : "center",
    },
    marketingDescription: {
        textAlign: props.isMobileOrTablet ? "left" : "center",
    },
    marketingContent: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        maxWidth: 816,
        width: "100%",
        gap: theme.spacing(2),
    },
    buttonContainer: {
        display: "flex",
        justifyContent: "flex-end",
        width: "100%",
        maxWidth: 1280,
        paddingInline: theme.spacing(2),
        [getThemeBreakpointByDeviceType(theme, EDeviceType.MOBILE)]: {
            width: "90%",
        },
    },
    button: {
        backgroundColor: theme.palette.common.white,
        color: theme.palette.primary.main,
        transition: "opacity 0.3s",
        "&:hover": {
            backgroundColor: theme.palette.common.white,
            opacity: 0.8,
        },
    },
}));
